import {
  friendAccept,
  friendCancel,
  friendReject,
  friendRequest,
  unblockAccount,
  type FriendAccount,
  type FriendRelationship,
} from '../transport/api'

/**
 * The search answer and the four relationship verbs, shared by the Add-a-human sheet
 * (search + scan) and the Group tab's request tray. email is the whole identity a person
 * types, reads off a QR, or sees in the tray — the backend never leaks iss/sub — so the
 * relationship decides the one button, and the button decides the call.
 */
export type SearchState =
  | { kind: 'searching'; email: string }
  | { kind: 'none'; email: string }
  /* EVERY account at the address — owner, 2026-09-14. One email can be two people
     (the same person at Google and at Apple, or two people who share nothing but an
     address), and the old shape could only hold one of them. `account` stays as the
     first, because the request tray and the scanner both read it and neither has an
     ambiguous address to show. */
  | {
      kind: 'found'
      email: string
      account: FriendAccount
      accounts: FriendAccount[]
      rel: FriendRelationship
    }
  | { kind: 'error'; email: string; detail: string }

export type Verb = 'request' | 'accept' | 'reject' | 'cancel' | 'unblock'

/** Deferred on purpose: the four api functions are touched only when a button fires,
 *  never at module load. A map literal would reference them at import, which breaks a
 *  partial api mock that omits them. */
/**
 * `account` is passed ONLY when there is one — owner, 2026-09-14. An address with a
 * single account (nearly all of them) produces exactly the call this made before,
 * argument for argument, so nothing downstream can tell the difference. Handing every
 * call a trailing `undefined` would have worked too, and the existing tests said so by
 * failing: they pin the call SHAPE, which is worth keeping honest.
 *
 * Still a ternary rather than a map literal, for this module's own reason: a map
 * references all four functions at import, which breaks a partial api mock that omits
 * them.
 */
export const runVerb = (verb: Verb, email: string, account?: string): Promise<unknown> =>
  // Unblock names the account by its handle — the only way the list or a search knows it (BLOCK, 2026-10-17).
  verb === 'unblock'
    ? unblockAccount(account ?? '')
    : verb === 'request'
    ? account
      ? friendRequest(email, account)
      : friendRequest(email)
    : verb === 'accept'
      ? account
        ? friendAccept(email, account)
        : friendAccept(email)
      : verb === 'reject'
        ? account
          ? friendReject(email, account)
          : friendReject(email)
        : account
          ? friendCancel(email, account)
          : friendCancel(email)

export const DONE: Record<Verb, (email: string) => string> = {
  request: (e) => `Request sent to ${e}`,
  accept: (e) => `You and ${e} are friends now`,
  reject: (e) => `Declined ${e}`,
  cancel: (e) => `Request to ${e} withdrawn`,
  unblock: (e) => `Unblocked ${e}`,
}

/** The backend's refusals, said in words a person can act on. */
const ERRORS: Record<string, string> = {
  no_such_account: 'No account has ever signed in with that email.',
  already_friends: 'You are already friends.',
  already_requested: 'A request is already pending.',
  cannot_friend_self: 'That is your own account.',
  no_such_request: 'That request is no longer there.',
  you_blocked_them: 'You blocked this account — unblock them first.',
  not_blocked: 'That account is not blocked.',
}

// bffJson throws a plain Error whose message is the BFF's detail-or-error string, so
// map the known codes to friendlier words and otherwise show what the server said.
export const errText = (e: unknown) =>
  e instanceof Error ? (ERRORS[e.message] ?? e.message) : 'something went wrong'
